import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { Alert, AlertSeverity, AlertCategory } from '@/types/api';

export interface AlertsParams {
  severity?: AlertSeverity;
  category?: AlertCategory;
  botId?: string;
  includeAcknowledged?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedAlerts {
  alerts: Alert[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useAlerts(params?: AlertsParams, opts?: { refetchInterval?: number }) {
  return useQuery<PaginatedAlerts>({
    queryKey: ['alerts', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.severity) searchParams.set('severity', params.severity);
      if (params?.category) searchParams.set('category', params.category);
      if (params?.botId) searchParams.set('botId', params.botId);
      if (params?.includeAcknowledged !== undefined) searchParams.set('includeAcknowledged', String(params.includeAcknowledged));
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      const res = await api.get(`/alerts${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    refetchInterval: opts?.refetchInterval,
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.patch(`/alerts/${id}/acknowledge`);
      return res.data;
    },
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ['alerts'] });
      const queries = qc.getQueriesData<PaginatedAlerts>({ queryKey: ['alerts'] });
      const snapshots = queries.map(([key, data]) => [key, data] as const);
      for (const [key, data] of queries) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          alerts: data.alerts.map((a) =>
            a.id === id ? { ...a, acknowledged: true } : a,
          ),
        });
      }
      return { snapshots };
    },
    onError: (_err, _id, context) => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          qc.setQueryData(key, data);
        }
      }
    },
  });
}

export function useAcknowledgeAllAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post('/alerts/acknowledge-all');
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}
